import React from 'react';
import { Route, Routes } from 'react-router';
import { PagePath } from '../navigation.enums';
import { OrganizationLevelType } from '../../enums';
import { PermissionType } from '../../enums/permission-type';
import { PageGuard } from '../guards/page.guard';
import { ActiveCompanySlugRedirect } from './routers.helpers';
import { useCompanySlugParamChange } from '../../library/hooks/useCompanySlugParamChange';
import { ManageRolesPage } from '../../pages/role-skill-management/roles/manage-roles.page';
import { RolesWizardPage } from '../../pages/role-skill-management/roles/wizard/roles-wizard.page';
import { CustomRoleStepperPage } from '../../pages/role-skill-management/roles/wizard/custom-role/custom-role-stepper.page';
import { ImportRoleStepperPage } from '../../pages/role-skill-management/roles/wizard/import-role/import-role-stepper.page';
import { KeplerAIStepperPage } from '../../pages/role-skill-management/roles/wizard/kepler-ai-role/kepler-ai-stepper.page';
import { ManageSkillsPage } from '../../pages/role-skill-management/skills/manage-skills.page';
import { ManageSkillCategoriesPage } from '../../pages/role-skill-management/skill-categories/manage-skill-categories.page';
import { SkillCategoriesCreatePage } from '../../pages/role-skill-management/skill-categories/skill-categories-create.page';
import { SkillCategoriesEditPage } from '../../pages/role-skill-management/skill-categories/skill-categories-edit.page';
import { SkillCategoriesViewPage } from '../../pages/role-skill-management/skill-categories/skill-categories-view.page';

export function RoleSkillManagementRouter(): JSX.Element {
  useCompanySlugParamChange(PagePath.roleSkillManagementBase);

  return (
    <Routes>
      <Route
        path={PagePath.roleSkillManagementAllRoles}
        element={
          <PageGuard
            organizationLevelTypesRequired={{
              permissionType: PermissionType.RoleSkillManagement,
              organizationLevels: [{ organizationLevelType: OrganizationLevelType.Company, multipleEntities: false }, { organizationLevelType: OrganizationLevelType.Department, multipleEntities: false }],
            }}
          >
            <ManageRolesPage />
          </PageGuard>
        }
      />
      <Route
        path={PagePath.roleSkillManagementAllSkills}
        element={
          <PageGuard
            organizationLevelTypesRequired={{
              permissionType: PermissionType.RoleSkillManagement,
              organizationLevels: [{ organizationLevelType: OrganizationLevelType.Company, multipleEntities: false }, { organizationLevelType: OrganizationLevelType.Department, multipleEntities: false }],
            }}
          >
            <ManageSkillsPage />
          </PageGuard>
        }
      />
      <Route
        path={PagePath.roleSkillManagementWizard}
        element={
          <PageGuard
            organizationLevelTypesRequired={{
              permissionType: PermissionType.RoleSkillManagement,
              organizationLevels: [{ organizationLevelType: OrganizationLevelType.Company, multipleEntities: false }, { organizationLevelType: OrganizationLevelType.Department, multipleEntities: false }],
            }}
          >
            <RolesWizardPage />
          </PageGuard>
        }
      />
      <Route
        path={PagePath.roleSkillManagementWizardKeplerAI}
        element={
          <PageGuard
            organizationLevelTypesRequired={{
              permissionType: PermissionType.RoleSkillManagement,
              organizationLevels: [{ organizationLevelType: OrganizationLevelType.Company, multipleEntities: false }, { organizationLevelType: OrganizationLevelType.Department, multipleEntities: false }],
            }}
          >
            <KeplerAIStepperPage />
          </PageGuard>
        }
      />
      <Route
        path={PagePath.roleSkillManagementWizardCustomRole}
        element={
          <PageGuard
            organizationLevelTypesRequired={{
              permissionType: PermissionType.RoleSkillManagement,
              organizationLevels: [{ organizationLevelType: OrganizationLevelType.Company, multipleEntities: false }, { organizationLevelType: OrganizationLevelType.Department, multipleEntities: false }],
            }}
          >
            <CustomRoleStepperPage />
          </PageGuard>
        }
      />
      <Route
        path={PagePath.roleSkillManagementWizardImportRole}
        element={
          <PageGuard
            organizationLevelTypesRequired={{
              permissionType: PermissionType.RoleSkillManagement,
              organizationLevels: [{ organizationLevelType: OrganizationLevelType.Company, multipleEntities: false }, { organizationLevelType: OrganizationLevelType.Department, multipleEntities: false }],
            }}
          >
            <ImportRoleStepperPage />
          </PageGuard>
        }
      />
      <Route
        path={PagePath.roleSkillManagementWizardUpdate}
        element={
          <PageGuard
            organizationLevelTypesRequired={{
              permissionType: PermissionType.RoleSkillManagement,
              organizationLevels: [{ organizationLevelType: OrganizationLevelType.Company, multipleEntities: false }, { organizationLevelType: OrganizationLevelType.Department, multipleEntities: false }],
            }}
          >
            <CustomRoleStepperPage />
          </PageGuard>
        }
      />
      <Route
        path={PagePath.roleSkillManagementSkillCategories}
        element={
          <PageGuard
            organizationLevelTypesRequired={{
              permissionType: PermissionType.RoleSkillManagement,
              organizationLevels: [{ organizationLevelType: OrganizationLevelType.Company, multipleEntities: false }, { organizationLevelType: OrganizationLevelType.Department, multipleEntities: false }],
            }}
          >
            <ManageSkillCategoriesPage />
          </PageGuard>
        }
      />
      <Route
        path={PagePath.roleSkillManagementSkillCategoriesCreate}
        element={
          <PageGuard
            organizationLevelTypesRequired={{
              permissionType: PermissionType.RoleSkillManagement,
              organizationLevels: [{ organizationLevelType: OrganizationLevelType.Company, multipleEntities: false }, { organizationLevelType: OrganizationLevelType.Department, multipleEntities: false }],
            }}
          >
            <SkillCategoriesCreatePage />
          </PageGuard>
        }
      />
      <Route
        path={PagePath.roleSkillManagementSkillCategoriesEdit}
        element={
          <PageGuard
            organizationLevelTypesRequired={{
              permissionType: PermissionType.RoleSkillManagement,
              organizationLevels: [{ organizationLevelType: OrganizationLevelType.Company, multipleEntities: false }, { organizationLevelType: OrganizationLevelType.Department, multipleEntities: false }],
            }}
          >
            <SkillCategoriesEditPage />
          </PageGuard>
        }
      />
      <Route
        path={PagePath.roleSkillManagementSkillCategoriesView}
        element={
          <PageGuard
            organizationLevelTypesRequired={{
              permissionType: PermissionType.RoleSkillManagement,
              organizationLevels: [{ organizationLevelType: OrganizationLevelType.Company, multipleEntities: false }, { organizationLevelType: OrganizationLevelType.Department, multipleEntities: false }],
            }}
          >
            <SkillCategoriesViewPage />
          </PageGuard>
        }
      />

      <Route path={PagePath.rootWildcard} element={<ActiveCompanySlugRedirect landingRedirectTo={`${PagePath.roleSkillManagementBase}${PagePath.roleSkillManagementAllRoles}`} />} />
    </Routes>
  );
}
